import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronRight } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Cell,
  Tooltip, LabelList, ResponsiveContainer,
} from 'recharts';
import { FullScreenChartModal, ExpandButton } from '../FullScreenChartModal/FullScreenChartModal';
import { MACRO_LABELS } from '../../constants/map';
import { resolveCityDataKey } from '../../services/companyFileReader';
import { scoreBarFill } from '../../utils/scoreColors';
import { scoreScaleGradient } from '../../../../constants/scoreScale';
import styles from './ComparePanel.module.css';

const C_TEAL  = '#273308';
const C_MUTED = '#76786C';
const C_LINE  = '#E5E5CC';

const scoreColor = (value) => scoreBarFill(value);

/** Score sits just to the right of the bar, with the same weight as the other labels. */
function ScoreValueLabel({ x = 0, y = 0, width = 0, height = 0, value, fontSize = 10 }) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return (
    <text
      x={x + width + 6}
      y={y + height / 2}
      fill={C_TEAL}
      stroke="none"
      fontSize={fontSize}
      fontWeight={900}
      fontFamily="Work Sans, sans-serif"
      dominantBaseline="middle"
    >
      {numeric.toFixed(1)}
    </text>
  );
}

/**
 * ComparePanel — ranked horizontal bar chart comparing all companies on the
 * selected macro / sub / breakdown metric. Competitors are anonymised.
 */
const ComparePanel = ({
  company,
  allCompanies,
  activeMacro,
  activeSub,
  activeBreakdown,
  selectedCity,
  showCompanyNames = false,
  onClose,
}) => {
  const [isChartExpanded, setIsChartExpanded] = useState(false);
  const [viewMode, setViewMode] = useState('competitors'); // 'competitors' | 'historical'

  const macroLabel = 
    activeMacro === 'indice_sintetico' ? 'INDICE SINTETICO' :
    activeMacro === 'servizi_fisici' ? 'SERVIZI FISICI' :
    activeMacro === 'servizi_digitali' ? 'SERVIZI DIGITALI' :
    MACRO_LABELS[activeMacro] ?? activeMacro.replace(/_/g, ' ');
    
  const subLabel   = activeSub ? activeSub.replace(/_/g, ' ') : null;
  const bdLabel    = activeBreakdown ? activeBreakdown.replace(/_/g, ' ') : null;
  const title      = bdLabel ?? subLabel ?? macroLabel;

  // Score extractor
  const buildChartDataForMacro = (macro) => {
    const isPhysicalMacro = ['linee_urbane', 'linee_extraurbane', 'fermate', 'punti_vendita', 'servizi_fisici'].includes(macro);
    const isDigitalMacro = ['call_center', 'sito_web', 'app'].includes(macro);

    const readCategoryScore = (cd) => {
      if (!cd) return null;
      if (activeSub && activeBreakdown) {
        const v = cd?.[macro]?.sub?.[activeSub]?.breakdown?.[activeBreakdown];
        return typeof v === 'number' ? v : null;
      }
      if (activeSub) {
        const v = cd?.[macro]?.sub?.[activeSub]?.punteggio_globale;
        return typeof v === 'number' ? v : null;
      }
      const v = cd?.[macro]?.punteggio_globale;
      return typeof v === 'number' ? v : null;
    };

    const getScoreForMacro = (c, cityName) => {
      if (macro === 'indice_sintetico') {
        const cittaObj = c.citta || c.città;
        if (cittaObj && cityName) {
          const cityScore = cittaObj[resolveCityDataKey(cittaObj, cityName)]?.indice_sintetico;
          if (typeof cityScore === 'number') return cityScore;
        }
        return c['indice sintetico'] ?? c.indice_sintetico ?? null;
      }
      if (macro === 'servizi_digitali') return c.punteggio_servizi_digitali ?? null;

      const cittaObj = c.citta || c.città;
      if (!cittaObj) return null;

      // Digital services are stored on the city, and are shared across a company's cities.
      if (isDigitalMacro) {
        if (cityName) {
          const fromCity = readCategoryScore(cittaObj[resolveCityDataKey(cittaObj, cityName)]);
          if (fromCity !== null) return fromCity;
        }
        for (const cd of Object.values(cittaObj)) {
          const score = readCategoryScore(cd);
          if (score !== null) return score;
        }
        return null;
      }

      const cd = cittaObj[cityName];
      if (!cd) return null;

      if (macro === 'servizi_fisici') return cd?.punteggio_servizi_fisici ?? null;
      return readCategoryScore(cd);
    };

    let cd = [];
    let anonN = 0;

    if (viewMode === 'competitors') {
      const dedupMap = {};
      allCompanies.forEach((c) => {
        if (!dedupMap[c.azienda] || dedupMap[c.azienda].anno < c.anno) dedupMap[c.azienda] = c;
      });

      const rawData = [];
      Object.values(dedupMap).forEach((c) => {
        const cittaObj = c.citta || c.città || {};
        const cities = Object.keys(cittaObj);
        const isCurrentCompany = c.azienda === company.azienda;

        if (isPhysicalMacro) {
          cities.forEach(cityName => {
            const score = getScoreForMacro(c, cityName);
            if (score !== null) {
              rawData.push({
                azienda: c.azienda,
                cityName: cityName,
                score: score,
                isCurrentCompany,
                isPrimary: isCurrentCompany && (!selectedCity || selectedCity === cityName)
              });
            }
          });
        } else {
          const indiceCity = (isDigitalMacro || (macro === 'indice_sintetico' && isCurrentCompany)) && selectedCity
            ? selectedCity
            : '';
          let score = getScoreForMacro(c, indiceCity);
          if (score === null && isDigitalMacro && isCurrentCompany) {
            score = getScoreForMacro(company, indiceCity);
          }
          if (score !== null) {
            rawData.push({
              azienda: c.azienda,
              cityName: null,
              score: score,
              isCurrentCompany,
              isPrimary: isCurrentCompany
            });
          }
        }
      });

      rawData.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

      cd = rawData.map((d, i) => {
        let label = '';
        if (showCompanyNames) {
          label = d.azienda;
          if (d.cityName && isPhysicalMacro) {
            label += ` ${d.cityName}`;
          }
        } else if (d.isCurrentCompany) {
          label = company.azienda;
          const companyCitiesCount = Object.keys(company.citta || company.città || {}).length;
          if (d.cityName && companyCitiesCount > 1 && isPhysicalMacro) {
            label += ` ${d.cityName}`;
          }
        } else {
          label = `Az. ${++anonN}`;
        }
        return {
          label,
          score: d.score,
          isCurrent: d.isCurrentCompany,
          isPrimary: d.isPrimary,
          rank: i + 1,
        };
      });
    } else {
      const rawData = [];
      const myHistory = allCompanies.filter(c => c.azienda === company.azienda);
      
      myHistory.forEach((c) => {
        const cittaObj = c.citta || c.città || {};
        const cities = Object.keys(cittaObj);
        
        if (isPhysicalMacro) {
          cities.forEach((cityName) => {
            if (selectedCity && cityName !== selectedCity) return;
            const score = getScoreForMacro(c, cityName);
            if (score !== null) {
              rawData.push({
                anno: c.anno,
                cityName: cityName,
                score: score,
                isCurrent: c.anno === company.anno,
                isPrimary: c.anno === company.anno && (!selectedCity || selectedCity === cityName)
              });
            }
          });
        } else {
          const indiceCity = (isDigitalMacro || macro === 'indice_sintetico') && selectedCity ? selectedCity : '';
          let score = getScoreForMacro(c, indiceCity);
          if (score === null && isDigitalMacro) {
            score = getScoreForMacro(company, indiceCity);
          }
          if (score !== null) {
            rawData.push({
              anno: c.anno,
              cityName: null,
              score: score,
              isCurrent: c.anno === company.anno,
              isPrimary: c.anno === company.anno
            });
          }
        }
      });

      rawData.sort((a, b) => a.anno - b.anno); 

      cd = rawData.map((d) => {
        let label = `Anno ${d.anno}`;
        const compCitiesCount = Object.keys(company.citta || company.città || {}).length;
        if (d.cityName && isPhysicalMacro && compCitiesCount > 1 && !selectedCity) {
           label += ` ${d.cityName}`;
        }
        return {
          label,
          score: d.score,
          isCurrent: d.isCurrent,
          isPrimary: d.isPrimary,
          rank: null,
        };
      });
    }

    return cd;
  };

  const macrosToRender = activeMacro === 'indice_sintetico' 
    ? ['indice_sintetico', 'servizi_fisici', 'servizi_digitali'] 
    : [activeMacro];

  const chartsData = macrosToRender.map(m => ({
    macro: m,
    data: buildChartDataForMacro(m)
  }));

  const mainData = chartsData[0].data;
  const myEntry = mainData.find((d) => d.isPrimary) || mainData.find((d) => d.isCurrent);
  const myRank  = myEntry?.rank ?? null;
  const myScore = myEntry?.score ?? null;
  const total   = mainData.length;

  const BAR_H  = 34;
  const mainYWidth = mainData.length > 0 ? Math.max(...mainData.map((d) => d.label.length)) * 6.5 + 8 : 100;

  return (
    <div className={styles.panel}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {/* Breadcrumb */}
          <div className={styles.breadcrumb}>
            <span>{macroLabel}</span>
            {subLabel && <><ChevronRight size={9} /><span className={styles.bdCrumb}>{subLabel}</span></>}
            {bdLabel   && <><ChevronRight size={9} /><span className={styles.bdCrumb}>{bdLabel}</span></>}
          </div>
          <h3 className={styles.title}>{viewMode === 'competitors' ? 'CLASSIFICA' : 'STORICO'}</h3>
          <div className={styles.badgeRow}>
            {myScore !== null && (
              <span className={styles.myScore} style={{ color: scoreColor(myScore) }}>
                {myScore.toFixed(2)}
              </span>
            )}
            {myRank !== null && (
              <div className={styles.rankBadge}>#{myRank} / {total}</div>
            )}
          </div>
        </div>
        <button onClick={onClose} className={styles.closeBtn}>
          <X size={14} color="#fff" />
        </button>
      </div>

      {/* Tabs */}
      <div className={styles.tabsRow} style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <button 
          onClick={() => setViewMode('competitors')}
          style={{ flex: 1, padding: '10px 0', background: 'transparent', border: 'none', borderBottom: viewMode === 'competitors' ? '2px solid #a59dff' : '2px solid transparent', color: viewMode === 'competitors' ? '#343A0A' : 'rgba(52, 58, 10, 0.5)', fontSize: '0.75rem', fontWeight: 800, cursor: 'pointer' }}
        >
          COMPETITORS
        </button>
        <button 
          onClick={() => setViewMode('historical')}
          style={{ flex: 1, padding: '10px 0', background: 'transparent', border: 'none', borderBottom: viewMode === 'historical' ? '2px solid #a59dff' : '2px solid transparent', color: viewMode === 'historical' ? '#343A0A' : 'rgba(52, 58, 10, 0.5)', fontSize: '0.75rem', fontWeight: 800, cursor: 'pointer' }}
        >
          STORICO
        </button>
      </div>

      {/* Legend */}
      <div className={styles.legend}>
        {viewMode === 'competitors' ? (
          <>
            <div className={styles.legendItem}>
              <div className={styles.legendDot} style={{ background: '#B4CD41' }} />
              <span>{showCompanyNames ? 'CLIENTE SELEZIONATO' : 'TUA AZIENDA'}</span>
            </div>
            <div className={styles.legendItem}>
              <div className={styles.legendDot} style={{ background: '#D5D5D5' }} />
              <span>{showCompanyNames ? 'ALTRE AZIENDE' : 'ALTRE (ANONIME)'}</span>
            </div>
          </>
        ) : (
          <>
            <div className={styles.legendItem}>
              <div className={styles.legendDot} style={{ background: scoreScaleGradient('to right'), width: 28, borderRadius: 99 }} />
              <span>PUNTEGGIO 1 → 10</span>
            </div>
            <div className={styles.legendItem}>
              <div className={styles.legendDot} style={{ background: '#fff', boxShadow: 'inset 0 0 0 2px #353A0A' }} />
              <span>{`ANNO ${company.anno}`}</span>
            </div>
          </>
        )}
      </div>

      {/* Chart */}
      <div className={styles.body}>
        {chartsData.length === 0 || chartsData[0].data.length === 0 ? (
          <div className={styles.empty}>NESSUN DATO DISPONIBILE</div>
        ) : (
          chartsData.map(({ macro, data }, chartIdx) => {
            if (data.length === 0) return null;
            
            const chartH = Math.max(180, data.length * BAR_H + 24);
            const yWidth = Math.max(...data.map((d) => d.label.length)) * 6.5 + 8;
            
            const sectionTitle = macro === 'indice_sintetico' ? null : 
                          macro === 'servizi_fisici' ? 'SERVIZI FISICI' : 
                          macro === 'servizi_digitali' ? 'SERVIZI DIGITALI' : null;

            return (
              <div key={macro} style={{ marginBottom: chartIdx < chartsData.length - 1 ? 40 : 0 }}>
                {sectionTitle && (
                  <h4 style={{ color: '#343A0A', fontSize: '11px', fontWeight: 800, marginBottom: 16, marginTop: 16, opacity: 0.8, letterSpacing: 1.5, paddingBottom: 6, borderBottom: '1px solid rgba(52, 58, 10, 0.1)' }}>
                    {sectionTitle}
                  </h4>
                )}
                <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, delay: chartIdx * 0.1 }} style={{ position: 'relative' }}>
                  {chartIdx === 0 && <ExpandButton onClick={() => setIsChartExpanded(true)} />}
                  <ResponsiveContainer width="100%" height={chartH}>
                  <BarChart layout="vertical"
                    data={data} margin={{ top: 12, right: 52, bottom: 4, left: 0 }}>
                    <XAxis type="number" domain={[0, 10]}
                      tick={{ fontSize: 9, fill: C_MUTED }} axisLine={false} tickLine={false} tickCount={6} />
                    <YAxis type="category" dataKey="label" width={Math.min(yWidth, 140)}
                      tick={{ fontSize: 10, fill: '#46483D', fontWeight: 700, fontFamily: 'Work Sans, sans-serif' }}
                      axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className={styles.tooltip}>
                            {d.rank !== null && <div className={styles.tooltipRank}>#{d.rank} di {data.length}</div>}
                            <div className={styles.tooltipScore} style={{ color: scoreColor(d.score) }}>
                              {d.score.toFixed(2)}<span className={styles.tooltipDenom}>/10</span>
                            </div>
                          </div>
                        );
                      }} />
                    <Bar dataKey="score" radius={[0, 5, 5, 0]} barSize={BAR_H - 8}>
                      <LabelList dataKey="score" content={(props) => <ScoreValueLabel {...props} fontSize={10} />} />
                      {data.map((d, i) => {
                        const isOtherCompany = viewMode === 'competitors' && !d.isCurrent;
                        return (
                        <Cell key={i} fill={isOtherCompany ? '#D5D5D5' : scoreColor(d.score)}
                          fillOpacity={isOtherCompany || d.isCurrent ? 1 : 0.55}
                          stroke={d.isPrimary ? '#353A0A' : 'none'} strokeWidth={d.isPrimary ? 1.5 : 0} />
                        );
                      })}
                    </Bar>
                  </BarChart>
                  </ResponsiveContainer>

                  <div className={styles.rankList}>
                    {data.map((d, i) => {
                      const isSelectedCityBar = d.isPrimary;
                      const isOtherCityBar = d.isCurrent && !d.isPrimary;
                      const isOtherCompany = viewMode === 'competitors' && !d.isCurrent;
                      const valueColor = isOtherCompany ? '#8E8E8E' : scoreColor(d.score);
                      return (
                      <div key={i} className={styles.rankItem}
                        style={{
                          background: isSelectedCityBar ? 'rgba(53,58,10,0.08)' : (isOtherCityBar ? 'rgba(53,58,10,0.04)' : '#f5f5ea'),
                          border: `1px solid ${isSelectedCityBar ? '#353A0A' : (isOtherCityBar ? 'rgba(53,58,10,0.28)' : C_LINE)}`,
                        }}>
                        {d.rank !== null && <span style={{ color: valueColor, fontWeight: 900 }}>#{d.rank}</span>}
                        <span style={{ color: d.isCurrent ? C_TEAL : C_MUTED, fontWeight: 700 }}>{d.label}</span>
                        <span style={{ color: valueColor, fontWeight: 900 }}>{d.score.toFixed(1)}</span>
                      </div>
                    )})}
                  </div>
                </motion.div>
              </div>
            );
          })
        )}
      </div>

      <AnimatePresence>
        {isChartExpanded && (
          <FullScreenChartModal
            isOpen={true}
            title={title}
            onClose={() => setIsChartExpanded(false)}
          >
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ResponsiveContainer width="100%" height={Math.max(400, mainData.length * BAR_H + 24)}>
                <BarChart layout="vertical" data={mainData} margin={{ top: 20, right: 60, bottom: 20, left: 20 }}>
                  <XAxis type="number" domain={[0, 10]}
                    tick={{ fontSize: 12, fill: C_MUTED }} axisLine={false} tickLine={false} tickCount={10} />
                  <YAxis type="category" dataKey="label" width={Math.min(mainYWidth + 40, 200)}
                    tick={{ fontSize: 14, fill: '#46483D', fontWeight: 700, fontFamily: 'Work Sans, sans-serif' }}
                    axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className={styles.tooltip} style={{ background: '#353A0A', padding: '12px', borderRadius: '8px', color: '#fff' }}>
                          <div className={styles.tooltipRank} style={{ fontSize: '14px', marginBottom: '4px' }}>#{d.rank} di {total}</div>
                          <div className={styles.tooltipScore} style={{ color: scoreColor(d.score), fontSize: '18px', fontWeight: 700 }}>
                            {d.score.toFixed(2)}<span className={styles.tooltipDenom} style={{ fontSize: '12px', opacity: 0.7 }}>/10</span>
                          </div>
                        </div>
                      );
                    }} />
                  <Bar dataKey="score" radius={[0, 6, 6, 0]} barSize={Math.max(BAR_H - 4, 30)}>
                    <LabelList dataKey="score" content={(props) => <ScoreValueLabel {...props} fontSize={14} />} />
                    {mainData.map((d, i) => {
                      const isOtherCompany = viewMode === 'competitors' && !d.isCurrent;
                      return (
                      <Cell key={i} fill={isOtherCompany ? '#D5D5D5' : scoreColor(d.score)}
                        fillOpacity={isOtherCompany || d.isCurrent ? 1 : 0.55}
                        stroke={d.isPrimary ? '#353A0A' : 'none'} strokeWidth={d.isPrimary ? 2 : 0} />
                      );
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </FullScreenChartModal>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ComparePanel;
